import { useState } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { FullScreenSheet } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { CtaRow, SolidCtaButton } from "@acto/taskpane/field-sales-2/components/primitives/SheetCta";
import {
  settledDuration,
  type DurationChoice,
  type HideableProduct,
  type SettledDuration,
} from "@acto/taskpane/field-sales-2/utils/productHide";
import { SelectedProductsSummary } from "./SelectedProductsSummary";
import { DurationField } from "./DurationField";
import { ProductHideReasonPicker, type ReasonDraft } from "./ProductHideReasonPicker";

const Content = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  paddingInline: theme.fieldSalesTwo.spacing[16],
}));

const SectionDivider = styled(Divider)(({ theme }) => ({
  width: "auto",
  marginInline: theme.fieldSalesTwo.spacing[8],
  marginBlock: theme.fieldSalesTwo.spacing[12],
}));

const Group = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[10],
}));

const Title = styled("h2")(({ theme }) => ({
  margin: 0,
  textAlign: "center",
  paddingInline: theme.fieldSalesTwo.spacing[20],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["20"],
  fontWeight: theme.fieldSalesTwo.font.weight["540"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["130"],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Subtitle = styled("p")(({ theme }) => ({
  margin: 0,
  textAlign: "center",
  paddingInline: theme.fieldSalesTwo.spacing[20],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["168"],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

interface ProductHideOverlayProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  products: HideableProduct[];
  onConfirm: (reason: ReasonDraft, duration: SettledDuration) => void;
  isSubmitting?: boolean;
  today?: Date;
}

function ProductHideDraft({
  products,
  onConfirm,
  isSubmitting,
  today,
}: Omit<ProductHideOverlayProps, "isOpen" | "onOpenChange">) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");
  const [duration, setDuration] = useState<DurationChoice>({ kind: "forever" });
  const [reason, setReason] = useState<ReasonDraft | null>(null);

  const settled = settledDuration(duration);
  const text = reason?.text.trim() ?? "";
  const submission = settled && reason && text ? { reason: { ...reason, text }, duration: settled } : null;

  return (
    <>
      <Content>
        <Group>
          <Title>{t("hideSelected", { count: products.length })}</Title>
          <Subtitle>{t("overlaySubtitle")}</Subtitle>
        </Group>
        <SelectedProductsSummary products={products} />
        <DurationField value={duration} onChange={setDuration} today={today} />
        <SectionDivider />
        <ProductHideReasonPicker value={reason} onChange={setReason} />
      </Content>
      <CtaRow>
        <SolidCtaButton
          type="button"
          disabled={!submission || isSubmitting}
          onClick={() => submission && onConfirm(submission.reason, submission.duration)}
        >
          {t("confirm")}
        </SolidCtaButton>
      </CtaRow>
    </>
  );
}

export function ProductHideOverlay({ isOpen, onOpenChange, ...draft }: ProductHideOverlayProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");

  return (
    <FullScreenSheet
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open && draft.isSubmitting) return;
        onOpenChange(open);
      }}
      title={t("hideSelected", { count: draft.products.length })}
      titleHidden
      closeAriaLabel={t("close")}
    >
      <ProductHideDraft {...draft} />
    </FullScreenSheet>
  );
}
